import { mande, type MandeError } from 'mande'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useTenant } from '@/composables/useTenant'

interface Similar {
  id: string
  title: string
}

interface Category {
  id: string
  title: string
  slug?: string
}

interface Content {
  id: string
  title: string
  published_at: string
  read_duration_minutes: number
  content: string
  similar?: Similar[]
  categories: Category[]
}

export function useContentBySlug(slug: string) {
  const config = useRuntimeConfig()
  const api = mande(`${config.public.apiBaseUrl}/content`)
  const language = useI18n().locale.value.split('-')[0]
  const content = ref<Content | null>(null)
  const loading = ref<boolean>(true)
  const error = ref<MandeError | null>(null)

  const fetchContent = async () => {
    try {
      const response = await api.get<{ payload: Content }>(slug, {
        headers: {
          'Accept-Language': language,
        },
        query: {
          tenant: useTenant().value,
        },
      })
      content.value = response.payload
    } catch (apiError) {
      error.value = apiError as MandeError
    } finally {
      loading.value = false
    }
  }

  return {
    content,
    fetchContent,
    loading,
    error,
  }
}

export function useVoteById() {
  const config = useRuntimeConfig()
  const api = mande(`${config.public.apiBaseUrl}/content`)
  const language = useI18n().locale.value.split('-')[0]
  const result = ref<{} | null>(null)
  const loading = ref<boolean>(true)
  const error = ref<MandeError | null>(null)

  const fetchVoteById = async (
    id: string,
    platform: string,
    helpful: boolean,
    reasons?: any,
    message?: string,
  ) => {
    try {
      const response = await api.post<{ payload: {} }>(
        `/${id}/feedback`,
        helpful
          ? {
              helpful,
            }
          : {
              helpful,
              reasons,
              message,
            },
        {
          headers: {
            'Accept-Language': language,
            'X-Client-Platform': platform,
          },
        },
      )
      result.value = response.payload
    } catch (apiError) {
      error.value = apiError as MandeError
    } finally {
      loading.value = false
    }
  }

  return {
    result,
    fetchVoteById,
    loading,
    error,
  }
}
